import Head from 'next/head'
import { withTranslation } from 'server/i18n'
import PropTypes from 'prop-types'
import { NextSeo } from 'next-seo'
import { Flex, Box } from 'reflexbox'
import getConfig from 'next/config'
import ReactMarkdown from 'react-markdown'
import { getSellFields } from 'helpers/wordpress/wp-api'

import PageTitle from 'components/PageTitle'
import BoatMainImage from 'components/BoatMainImage'
import ServicesBlock from 'components/ServicesBlock'
import InquiryForm from 'components/InquiryForm'
import BlockTitle from 'components/BlockTitle'
import PromoBlock from 'components/PromoBlock'
import Button from 'components/Button'
import NewsletterPromo from 'components/NewsletterPromo'

import layoutTheme from 'theme/layout-theme'

import MarketResearchIcon from 'public/icons/market-research.svg'
import ListingIcon from 'public/icons/listing.svg'
import SalesStrategyIcon from 'public/icons/sales-strategy.svg'
import PhotoshootingIcon from 'public/icons/photoshooting.svg'
import DigitalMarketingIcon from 'public/icons/digital-marketing.svg'
import LeadManagementIcon from 'public/icons/lead-management.svg'
import ClosingAdministrationIcon from 'public/icons/closing-administration.svg'
import { useMemo } from 'react'

const { publicRuntimeConfig } = getConfig()
const { CLOUDINARY_URL, DOMAIN_ROOT } = publicRuntimeConfig

const addIdToItem = (item, index) => {
    if (typeof item.id !== 'undefined') {
        return item
    }
    return {
        ...item,
        id: index + 1
    }
}
const getContent = t => {
    const sell = [
        {
            icon: <MarketResearchIcon />,
            text: t('manage-page:sell-market')
        },
        {
            icon: <ListingIcon />,
            text: t('manage-page:sell-listing')
        },
        {
            icon: <SalesStrategyIcon />,
            text: t('manage-page:sell-sales')
        },
        {
            icon: <PhotoshootingIcon />,
            text: t('manage-page:sell-photoshooting')
        },
        {
            icon: <DigitalMarketingIcon />,
            text: t('manage-page:sell-digital')
        },
        {
            icon: <LeadManagementIcon />,
            text: t('manage-page:sell-lead')
        },
        {
            icon: <ClosingAdministrationIcon />,
            text: t('manage-page:sell-closing')
        }
    ]

    return {
        sell: sell.map(addIdToItem)
    }
}

const SellPage = ({
    canonical,
    t,
    i18n,
    askUsBlock,
    contactBox,
    expertiseTextBox,
    introTextDe,
    introTextEn,
    meetTheTeamDe,
    meetTheTeamEn,
    strategyTextDe,
    strategyTextEn,
    htmlBoxDe,
    htmlBoxEn
}) => {
    const content = useMemo(() => {
        return getContent(t)
    }, [i18n.language])

    const seoData = useMemo(
        () => ({
            title: `${t('meta-data:sell-title')}`,
            description: t('meta-data:sell-description'),
            canonical: t('meta-data:sell-url'),
            openGraph: {
                title: `${t('meta-data:sell-title')}`,
                description: t('meta-data:sell-description'),
                url: t('meta-data:sell-url'),
                images: [
                    {
                        url: `${CLOUDINARY_URL}/v1582632369/Static/sell-image_hace64.jpg`
                    }
                ]
            },
            languageAlternates: [
                {
                    hrefLang: 'en',
                    href: `${DOMAIN_ROOT}/sell`
                },
                {
                    hrefLang: 'de',
                    href: `${DOMAIN_ROOT}/de/sell`
                }
            ]
        }),
        [i18n.language]
    )

    return (
        <div>
            <NextSeo {...seoData} />
            <Head>
                <script
                    language="javascript"
                    type="text/javascript"
                    /* eslint-disable-next-line react/no-danger */
                    dangerouslySetInnerHTML={{
                        __html: `(function (id, f, t, ws, ms_tr_il_08, ms_tr_il_w_01) { var tr = function (cb) { var count = 0; var callback = function () { if (count == 0) { count++; if (w) { w.w(id, t, cb); } } }; var ts = document.createElement('script'); ts.src = ws; ts.type = 'text/javascript'; ts.onload = callback; ts.onreadystatechange = function () { if (this.readyState == 'complete' || this.readyState == 'loaded') { callback(); } }; var head = document.getElementsByTagName('head')[0]; head.appendChild(ts); }; if (typeof ms_tr_il_08 === 'function') { if (ms_tr_il_w_01 === null) { tr(function() { ms_tr_il_08(id, f, t); }); } else { ms_tr_il_w_01.w(id, t, function(websiteVisitedParams) { ms_tr_il_08(id, f, t, websiteVisitedParams); }, null); } } else { tr(); }})('kBAM47FuW-dk9EuEOQ_fHpnv95N23L2ly5CuvHZauu0', 'https://a78037f57059413b89ee955ac3aa54f4.svc.dynamics.com/f', 'https://a78037f57059413b89ee955ac3aa54f4.svc.dynamics.com/t', 'https://a78037f57059413b89ee955ac3aa54f4.svc.dynamics.com/t/w', typeof ms_tr_il_08 === "undefined" ? null : ms_tr_il_08, typeof ms_tr_il_w_01 === "undefined" ? null : ms_tr_il_w_01);`
                    }}
                />
            </Head>

            <Box theme={layoutTheme} px={40} mt={[35, null, 70, 100, 150]} mb={[35, null, 70, 80]} variant="center1280">
                <PageTitle
                    title={t('sell-page:sell-title')}
                    subtitle={t('sell-page:sell-subtitle')}
                    titleTag="h1"
                    subtitleTag="h2"
                />
            </Box>

            <Box theme={layoutTheme} px="20px" mb={['40px', null, null, '180px']} maxWidth="1600px" mx="auto">
                <BoatMainImage image={`${CLOUDINARY_URL}/v1582632369/Static/sell-image_hace64.jpg`} alt="SELL ALT" />
            </Box>

            <Flex
                theme={layoutTheme}
                flexDirection={['column', null, null, 'row']}
                px={40}
                variant="center1360"
                justifyContent="space-between"
                mb={[null, null, null, 100]}>
                <Box
                    theme={layoutTheme}
                    fontSize={['20px', null, null, null, 24]}
                    color="#E3B05F"
                    mb={[20]}
                    maxWidth={240}
                    as="h3"
                    fontFamily="Poppins">
                    {t('sell-page:lets-sell')}
                </Box>

                <Box theme={layoutTheme} maxWidth={[null, null, null, '50%']}>
                    <Box theme={layoutTheme} as="p" mb={20} fontSize={[14, null, null, 20]}>
                        {i18n.language === 'en' ? introTextEn : introTextDe}
                    </Box>
                </Box>
            </Flex>

            <ServicesBlock
                data={content.sell}
                title={t('sell-page:dont-worry')}
                text={i18n.language === 'en' ? strategyTextEn : strategyTextDe}
                noBg
            />

            <Box theme={layoutTheme} px={20} mt={80} mb={[80, null, null, 230]} variant="center1600">
                <PromoBlock
                    image={`${CLOUDINARY_URL}/v1582632465/Static/sell-promo_a16elf.jpg`}
                    title={t('sell-page:who-handles-selling')}
                    subtitle={t('sell-page:meet-our-team')}
                    text={i18n.language === 'en' ? meetTheTeamEn : meetTheTeamDe}
                    buttonText={t('sell-page:meet-us')}
                    buttonLink="/about"
                />
            </Box>

            <Box theme={layoutTheme} px={20} mt={[80, null, null, 140]} mb={[80, null, null, 140]} variant="center1600">
                <NewsletterPromo
                    title={i18n.language === 'en' ? askUsBlock.titleEn : askUsBlock.titleDe}
                    subtitle={i18n.language === 'en' ? askUsBlock.textEn : askUsBlock.textDe}
                    buttonLabel={t('sell-page:send-request')}
                    link="/inquiry?type=sell"
                />
            </Box>

            <Box theme={layoutTheme} px={20} maxWidth={1440} mx="auto" mb={[80, null, null, 150, 250]}>
                <Box theme={layoutTheme} mb={[0, 0, 0, 60]}>
                    <BlockTitle title={i18n.language === 'en' ? expertiseTextBox.titleEn : expertiseTextBox.titleDe} />
                    {i18n.language === 'en' ? expertiseTextBox.textEn : expertiseTextBox.textDe}
                    <br />
                    <br />
                    <Button variation="tertiary" link="/inquiry?type=sell">
                        {i18n.language === 'en' ? expertiseTextBox.buttonLabelEn : expertiseTextBox.buttonLabelDe}
                    </Button>
                </Box>
            </Box>

            <Box theme={layoutTheme} px={20} maxWidth={1440} mx="auto" mb={[80, null, null, 150, 250]}>
                <Box theme={layoutTheme} mb={[0, 0, 0, 60]}>
                    <BlockTitle
                        title={i18n.language === 'en' ? contactBox.titleEn : contactBox.titleDe}
                        subtitle={i18n.language === 'en' ? contactBox.textEn : contactBox.textDe}
                    />
                </Box>
                <InquiryForm type="sell" />
            </Box>

            <Box theme={layoutTheme} px={20} maxWidth={1440} mx="auto" mb={[80, null, null, 150, 250]}>
                <Box theme={layoutTheme} mb={[0, 0, 0, 60]}>
                    <article>
                        <ReactMarkdown source={i18n.language === 'en' ? htmlBoxEn : htmlBoxDe} escapeHtml={false} />
                    </article>
                </Box>
            </Box>
        </div>
    )
}

SellPage.getInitialProps = async ({ req: { i18n } = {} }) => {
    const sellPageAcf = await getSellFields()

    const askUsBlock = sellPageAcf?.sells?.nodes[0]?.acf?.askUsImageBlock
    const contactBox = sellPageAcf?.sells?.nodes[0]?.acf?.contact
    const expertiseTextBox = sellPageAcf?.sells?.nodes[0]?.acf?.expertiseTextBox
    const introTextDe = sellPageAcf?.sells?.nodes[0]?.acf?.introTextDe
    const introTextEn = sellPageAcf?.sells?.nodes[0]?.acf?.introTextEn
    const meetTheTeamDe = sellPageAcf?.sells?.nodes[0]?.acf?.meetTheTeamDe
    const meetTheTeamEn = sellPageAcf?.sells?.nodes[0]?.acf?.meetTheTeamEn
    const strategyTextDe = sellPageAcf?.sells?.nodes[0]?.acf?.strategyTextDe
    const strategyTextEn = sellPageAcf?.sells?.nodes[0]?.acf?.strategyTextEn
    const htmlBoxEn = sellPageAcf?.sells?.nodes[0]?.acf?.htmlBoxEn
    const htmlBoxDe = sellPageAcf?.sells?.nodes[0]?.acf?.htmlBoxDe

    return {
        askUsBlock,
        contactBox,
        expertiseTextBox,
        introTextDe,
        introTextEn,
        meetTheTeamDe,
        meetTheTeamEn,
        strategyTextDe,
        strategyTextEn,
        htmlBoxDe,
        htmlBoxEn,
        canonical: i18n ? i18n.getFixedT('en')('meta-data:sell-url') : undefined,
        namespacesRequired: ['common', 'sell-page', 'manage-page', 'meta-data']
    }
}

SellPage.propTypes = {
    canonical: PropTypes.string,
    i18n: PropTypes.oneOfType([() => null, PropTypes.object]).isRequired,
    t: PropTypes.func.isRequired,
    askUsBlock: PropTypes.shape({
        textDe: PropTypes.string,
        textEn: PropTypes.string,
        titleDe: PropTypes.string,
        titleEn: PropTypes.string
    }),
    contactBox: PropTypes.shape({
        textDe: PropTypes.string,
        textEn: PropTypes.string,
        titleDe: PropTypes.string,
        titleEn: PropTypes.string
    }),
    expertiseTextBox: PropTypes.shape({
        textDe: PropTypes.string,
        textEn: PropTypes.string,
        titleDe: PropTypes.string,
        titleEn: PropTypes.string,
        buttonLabelDe: PropTypes.string,
        buttonLabelEn: PropTypes.string
    }),
    introTextDe: PropTypes.string,
    introTextEn: PropTypes.string,
    meetTheTeamDe: PropTypes.string,
    meetTheTeamEn: PropTypes.string,
    strategyTextDe: PropTypes.string,
    strategyTextEn: PropTypes.string,
    htmlBoxDe: PropTypes.string,
    htmlBoxEn: PropTypes.string
}

SellPage.defaultProps = {
    canonical: undefined,
    askUsBlock: {},
    contactBox: {},
    expertiseTextBox: {},
    introTextDe: undefined,
    introTextEn: undefined,
    meetTheTeamDe: undefined,
    meetTheTeamEn: undefined,
    strategyTextDe: undefined,
    strategyTextEn: undefined,
    htmlBoxDe: undefined,
    htmlBoxEn: undefined
}

export default withTranslation(['common', 'sell-page', 'manage-page', 'meta-data'])(SellPage)
